import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} KarimHeaderRefs
 * @property {HTMLElement} [cartCount] - Badge con el contador de items en el carrito
 */

/**
 * Componente del navbar Karim Cosmetics.
 * Mantiene sticky, expone --header-height y observa scroll para
 * toggle de "stuck" (para cambio de background transparente).
 * @extends {Component<KarimHeaderRefs>}
 */
class HeaderComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {number} */
  #lastY = 0;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {number | null} */
  #hideTimeout = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize || !entry.borderBoxSize[0]) return;
    const height = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${height}px`);
  });

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.#setupSticky();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver.disconnect();
    this.#observer?.disconnect();
    if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
    if (this.#hideTimeout) clearTimeout(this.#hideTimeout);
  }

  #setupSticky() {
    const mode = this.getAttribute('sticky') || 'always';
    if (mode === 'none') return;

    // Use sentinel intersection to flip "stuck"
    const sentinel = document.createElement('div');
    sentinel.setAttribute('data-header-sentinel', '');
    sentinel.style.cssText = 'position: relative; height: 1px; width: 1px; pointer-events: none;';
    this.parentNode?.insertBefore(sentinel, this);

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const stuck = !entry.isIntersecting;
          this.toggleAttribute('data-stuck', stuck);
        });
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );
    this.#observer.observe(sentinel);

    if (mode === 'scroll-up') {
      this.#lastY = window.scrollY;
      window.addEventListener('scroll', this.#onScroll, { passive: true });
    }
  }

  #onScroll = () => {
    if (this.#scrollRafId) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      const y = window.scrollY;
      const delta = y - this.#lastY;
      this.#lastY = y;

      if (y <= 0) {
        this.classList.remove('is-hidden');
        this.#offscreen = false;
        return;
      }

      if (delta > 4) {
        // scrolling down: hide
        if (!this.#offscreen) {
          this.#offscreen = true;
          if (this.#hideTimeout) clearTimeout(this.#hideTimeout);
          this.#hideTimeout = window.setTimeout(() => this.classList.add('is-hidden'), 80);
        }
      } else if (delta < -4) {
        // scrolling up: show
        if (this.#offscreen) {
          this.#offscreen = false;
          if (this.#hideTimeout) clearTimeout(this.#hideTimeout);
          this.classList.remove('is-hidden');
        }
      }
    });
  };
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/**
 * Updates the Karim header cart count bubbles in real time.
 * @param {number} count
 */
function updateKarimCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = String(count);
    el.classList.toggle('hidden', count === 0);
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;
      updateKarimCartCount(count);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[karim-header] Event promise rejected:', error);
    });
});

// On load, ensure CSS vars are set even before resize triggers
onDocumentLoaded(() => {
  const header = document.querySelector('header-component.karim-header');
  if (header && !document.body.style.getPropertyValue('--header-height')) {
    document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
    document.body.style.setProperty('--header-group-height', `${header.offsetHeight}px`);
  }
});